/**
 * imageService.js - Service for managing image uploads
 * 
 * @description
 * This service provides functions for uploading, retrieving and deleting 
 * profile and banner images for users.
 */

import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../utils/constants';

/**
 * Upload a profile image for a user
 * 
 * @param {string} userId - The user's ID
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - The response with the image URL
 */
export const uploadProfileImage = async (userId, imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  
  console.log('🔄 Profile image upload request:', {
    endpoint: `${API_BASE_URL}/api/user/profile-image`,
    fileName: imageFile.name,
    fileSize: imageFile.size,
    fileType: imageFile.type,
    authHeaders: getAuthHeader()
  });
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/profile-image`, {
      method: 'POST',
      headers: {
        ...getAuthHeader()
      },
      body: formData
    });
    
    console.log('📡 Profile image upload response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      console.error('❌ Profile image upload error:', errorData);
      throw new Error(errorData.error || 'Failed to upload image');
    }
    
    const result = await response.json();
    console.log('✅ Profile image upload success:', result);
    return result;
  } catch (error) {
    console.error('Profile image upload failed:', error);
    throw error;
  }
};

/**
 * Upload a banner image for a user
 * 
 * @param {string} userId - The user's ID
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - The response with the image URL
 */
export const uploadBannerImage = async (userId, imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  
  console.log('🔄 Banner image upload request:', {
    endpoint: `${API_BASE_URL}/api/user/banner-image`,
    fileName: imageFile.name,
    fileSize: imageFile.size,
    fileType: imageFile.type,
    authHeaders: getAuthHeader()
  });
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/banner-image`, {
      method: 'POST',
      headers: {
        ...getAuthHeader()
      },
      body: formData
    });
    
    console.log('📡 Banner image upload response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      console.error('❌ Banner image upload error:', errorData);
      throw new Error(errorData.error || 'Failed to upload image');
    }
    
    const result = await response.json();
    console.log('✅ Banner image upload success:', result);
    return result;
  } catch (error) {
    console.error('Banner image upload failed:', error);
    throw error;
  }
};

/**
 * Delete a user's image (profile or banner)
 * 
 * @param {string} userId - The user's ID
 * @param {string} imageType - The type of image ('profile' or 'banner')
 * @returns {Promise<Object>} - The response
 */
export const deleteUserImage = async (userId, imageType) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/users/${userId}/images?type=${imageType}`, 
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeader()
        }
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to delete ${imageType} image`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Delete ${imageType} image failed:`, error);
    throw error;
  }
};

/**
 * Get a user's public profile
 * 
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - The user's public profile
 */
export const getPublicProfile = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/public-profile`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch public profile');
    }
    
    const result = await response.json();
    return result.profile;
  } catch (error) {
    console.error('Failed to fetch public profile:', error);
    throw error;
  }
};
